<template>
  <v-sheet>
    <v-row>
      <v-col
        sm="12"
        md="6"
      >
        <v-card
          theme="dark"
          class="ma-2"
        >
          <template #prepend>
            <div class="text-h4 text-accent pa-1">
              Songbook PDFs
            </div>
          </template>
          <div class="d-flex fill">
            <v-text-field
              v-model="search"
              label="Search for Artist or Song"
              prepend-inner-icon="fa-sharp-duotone fa-search"
              @update:model-value="doSearch"
            />

            <v-icon-btn
              v-tooltip:bottom="'Show Indexed Only'"
              icon="fa-sharp-duotone fa-album-collection"
              :active="toggleIndex"
              @click="toggleIndex = !toggleIndex"
            />
            <v-icon-btn
              v-tooltip:bottom="'List Songbooks with Searched Song'"
              icon="fa-sharp-duotone fa-magnifying-glass-music"
              :active="toggleSongSearch"
              @click="toggleSongSearch = !toggleSongSearch"
            />
            <v-icon-btn
              v-tooltip:bottom="'Change Sorting Order'"
              :icon="
                sortOrder === 'asc'
                  ? 'fa-sharp-duotone fa-arrow-down-a-z'
                  : 'fa-sharp-duotone fa-arrow-down-z-a'
              "
              @click="changeOrder"
            />
          </div>
          <v-card
            v-if="files.length === 1"
            title="Found Songbook:"
            :subtitle="files[0].name"
            density="compact"
            rounded="lg"
            variant="tonal"
            class="ma-4"
            @click="loadbook(files[0].url)"
          >
            <v-list density="compact">
              <v-list-item
                v-for="(song, f) in files[0].songs"
                :key="f"
              >
                <template #prepend>
                  <v-avatar>
                    <v-icon
                      icon="fa-sharp-duotone fa-music"
                      pull="left"
                      size="sm"
                    />
                    {{ f + 1 }}
                  </v-avatar>
                </template>
                {{ song.title }}
              </v-list-item>
            </v-list>
          </v-card>

          <v-card
            v-if="search !== '' && toggleSongSearch"
            title="Song Search:"
            :subtitle="search"
            density="compact"
            rounded="lg"
            variant="outlined"
            class="ma-4"
          >
            <v-list density="compact">
              <v-list-item
                v-for="(album, g) in files"
                :key="g"
                @click="loadbook(files[g].url)"
              >
                <template #prepend>
                  <v-avatar>
                    <v-icon
                      icon="fa-sharp-duotone fa-album"
                      pull="left"
                      size="sm"
                    />
                  </v-avatar>
                </template>
                {{ album.name }}
              </v-list-item>
            </v-list>
          </v-card>

          <v-data-table
            v-model:sort-by="sortBy"
            hide-default-header
            :items="files"
            item-value="url"
            return-object
            :loading="appStore.loading"
            :height="getTableHeight"
          >
            <template #item="{ item }">
              <v-card
                :key="item.name"
                density="compact"
                variant="tonal"
                class="ma-1"
              >
                <v-list-item>
                  <template #title>
                    {{ parseTitle(item.name) }}
                  </template>
                  <template #append>
                    <div class="d-flex justify-self-end">
                      <v-btn
                        v-tooltip:bottom="'Album Tracks List'"
                        icon="fa-sharp-duotone fa-music"
                        size="xs"
                        class="ma-1"
                        :disabled="item.songs.length < 1"
                        @click="showlist(item.name, item.songs)"
                      />
                      <v-btn
                        v-tooltip:bottom="'Open Songbook'"
                        icon="fa-sharp-duotone fa-book-open"
                        size="xs"
                        class="ma-1"
                        @click="loadsong(item.url, 0)"
                      />
                      <v-btn
                        v-tooltip:bottom="'Download Songbook'"
                        icon="fa-sharp-duotone fa-file-pdf"
                        size="xs"
                        :href="item.url"
                        target="_new"
                        class="ma-1"
                        download
                      />
                    </div>
                  </template>
                </v-list-item>
              </v-card>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
      <v-col
        sm="12"
        md="6"
      >
        <v-card
          variant="outlined"
          class="ma-2"
        >
          <v-card-title class="text-h5">
            {{ parseTitle(albumName) }}
          </v-card-title>
          <v-card-text>
            <v-virtual-scroll
              :height="getHeight"
              :items="playlist"
            >
              <template #default="{ item }">
                <v-list-item
                  base-color="secondary"
                  color="accent"
                  @click="
                    loadsong(
                      `https://data.tdot.men/pdf/${albumName}.pdf`,
                      getIndex(item)
                    )
                  "
                >
                  <template #prepend>
                    <v-avatar>
                      {{ getIndex(item) }}
                    </v-avatar>
                  </template>
                  <v-list-item-title>
                    {{ item.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.destination }}
                  </v-list-item-subtitle>
                </v-list-item>
              </template>
            </v-virtual-scroll>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-footer
      class="text-center d-flex flex-column ga-2 py-4"
      color="indigo-lighten-1"
    >
      <div class="text-caption font-weight-regular opacity-60">
        This site, this web application as well as all contents available here
        are only for private use only. Any usage by the public is strictly
        prohibited.
      </div>
    </v-footer>
  </v-sheet>
</template>

<script setup lang="ts">
import axios from "axios";
import { useRouter } from "vue-router";
import { ref, computed, onMounted, watchEffect } from "vue";
import { useAppStore } from "../stores/app";
// import { useDisplay } from "vuetify";
import { type FileDetail, type Song } from "../components/Types";

const router = useRouter();
// const { mobile } = useDisplay();

const appStore = useAppStore();
// const showDrawer = computed(() => {
//   return appStore.drawer;
// });
const toggleSongSearch = ref(false);
const toggleIndex = ref(false);
// const expand = ref(false);

const files = ref([] as FileDetail[]);
const search = ref("");
// const sortBy = ref([{ key: "name", order: "asc" }]);
const sortOrder = ref("asc");

const sortBy = computed(() => {
  return [{ key: "name", order: sortOrder.value }];
});
const playlist = ref([] as Song[]);
const albumName = ref("");

const getHeight = computed(() => {
  return window.innerHeight - 150;
});

const getTableHeight = computed(() => {
  return window.innerHeight - 310;
});

function changeOrder() {
  sortOrder.value === "asc"
    ? (sortOrder.value = "desc")
    : (sortOrder.value = "asc");
}

function parseTitle(book: string) {
  if (book.includes("-")) {
    return book.replace("-", " ").replace(/\B([A-Z])\B/g, " $1");
  } else {
    return book.replace(/\B([A-Z])\B/g, " $1");
  }
}

function getIndex(item: Song) {
  return playlist.value.indexOf(item) + 1;
}
// function toggleDrawer() {
//   if (appStore.drawer) {
//     appStore.drawer = false;
//   } else {
//     appStore.drawer = true;
//   }
// }

function loadbook(book: string) {
  appStore.file = book;
  appStore.fileName = book
    .replace("https://data.tdot.men/pdf/", "")
    .replace(".pdf", "");
  appStore.currentPage = 1;
  // toggleDrawer();
  router.push("/viewer/" + appStore.fileName);
}

function loadsong(book: string, track: number) {
  appStore.file = book;
  appStore.fileName = book
    .replace("https://data.tdot.men/pdf/", "")
    .replace(".pdf", "");
  appStore.currentPage = 1;
  appStore.currentTrack = track;
  // toggleDrawer();
  router.push("/viewer/" + appStore.fileName);
}
function showIndexedOnly() {
  files.value = appStore.showIndexedOnly();
}

function doSearch() {
  if (search.value && search.value !== "") {
    files.value = appStore.find(search.value);
  }
}

function showlist(name: string, songs: Song[]) {
  playlist.value = songs;
  albumName.value = name;
}

watchEffect(() => {
  if (search.value === null || search.value === "") {
    files.value = appStore.files;
  }

  if (toggleIndex.value === true) {
    showIndexedOnly();
  }
});

onMounted(async () => {
  appStore.loading = true;
  if (appStore.files.length === 0) {
    // const newfiles: File[] = [];
    const data = await axios.get("https://data.tdot.men/pdf/filelist.json");

    const fileitems = data.data;
    // fileitems.forEach((f) => {
    //   f.songs = [];
    // });

    // const text = JSON.stringify(fileitems);
    // const filename = "fileitems.json";
    // const element = document.createElement('a');
    // element.setAttribute('href', 'data:application/json;charset=utf-8,' + encodeURIComponent(text));
    // element.setAttribute('download', filename);

    // element.style.display = 'none';
    // document.body.appendChild(element);

    // element.click();
    // document.body.removeChild(element);
    appStore.files = fileitems;
    files.value = fileitems;
  } else {
    files.value = appStore.files;
  }

  appStore.loading = false;

  // console.log(appStore.find("enjoy"));
});
</script>
